/**
 * Package-private dialogs of the note lane (W2-03 note-lane), rendered from the lane session (`session.dialog`) by
 * LaneShellHost, which the shell mounts in every editor workspace: "Insert beats at the playhead" (length + scope),
 * "Remove the selected time range" (scope) and "Rename marker". Each resolves the promise its command awaits (null =
 * cancelled); Esc and the close button cancel. Enter in the text field confirms: the field commits its entry first
 * (BeatField / TextField handle Enter on the input), then the dialog submits with it; an entry the field cannot read
 * stays for fixing and does not submit.
 */

import { useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Beats } from '../../core/time/beat';
import type { Beat } from '../../core/time/types';
import type { LineId } from '../../core/model/types';
import type { AppServices } from '../../app/registry/types';
import { useT } from '../../i18n/useT';
import { BeatField } from '../../ui/BeatField';
import { Button } from '../../ui/Button';
import { Checkbox } from '../../ui/Checkbox';
import { Dialog } from '../../ui/Dialog';
import { Segmented } from '../../ui/Segmented';
import { TextField } from '../../ui/TextField';
import { useDocSelect, useStore, sameArray } from './hooks';
import { m } from './noteLane.messages';
import {
  activeLineOf,
  beatCountText,
  beatText,
  laneSession,
  tempoOfStore,
  type LaneDialog,
  type TimeScope,
} from './laneSession';
import s from './LaneDialogs.module.css';

type ScopeKind = 'all' | 'active' | 'chosen';

function ScopeChooser(props: {
  readonly services: AppServices;
  readonly kind: ScopeKind;
  readonly chosen: readonly LineId[];
  onKind(k: ScopeKind): void;
  onChosen(lines: readonly LineId[]): void;
}) {
  const { services } = props;
  const t = useT();
  const store = useStore(services);
  const active = activeLineOf(services);
  const lines = useDocSelect(
    store,
    ['lines', 'lineOrder'],
    (doc, idx) =>
      doc.lineOrder.map((id) => {
        const name = doc.lines.get(id)?.name.trim() ?? '';
        return { id, label: name === '' ? t(m.lineUnnamed, { index: idx.lineIndex(id) }) : name };
      }),
    [],
    (a, b) =>
      sameArray(
        a.map((x) => `${String(x.id)}:${x.label}`),
        b.map((x) => `${String(x.id)}:${x.label}`),
      ),
  );
  const activeName = lines.find((l) => l.id === active)?.label ?? t(m.noLine);
  const options = [
    { value: 'all' as const, label: t(m.scopeAll) },
    ...(active === null ? [] : [{ value: 'active' as const, label: t(m.scopeActive, { line: activeName }) }]),
    { value: 'chosen' as const, label: t(m.scopeChosen) },
  ];
  return (
    <div className={s.scope}>
      <Segmented<ScopeKind>
        label={t(m.scopeLabel)}
        value={props.kind}
        options={options}
        onChange={(k) => {
          props.onKind(k);
        }}
      />
      {props.kind === 'chosen' && (
        <div className={s.lineList} role="group" aria-label={t(m.scopeChosen)}>
          {lines.map((l) => (
            <Checkbox
              key={l.id}
              label={l.label}
              checked={props.chosen.includes(l.id)}
              onChange={(on) => {
                props.onChosen(on ? [...props.chosen, l.id] : props.chosen.filter((x) => x !== l.id));
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function scopeOf(kind: ScopeKind, active: LineId | null, chosen: readonly LineId[]): TimeScope | null {
  if (kind === 'all') return 'all';
  if (kind === 'active') return active === null ? null : [active];
  return chosen.length === 0 ? null : chosen;
}

/**
 * Enter in a dialog's text input, after the field handled it (bubbling): true when the dialog should submit. The
 * field marks an entry it could not parse `aria-invalid` (the text stays for fixing); an empty field restores its
 * value instead of committing.
 */
export function isConfirmEnter(e: ReactKeyboardEvent): boolean {
  if (e.key !== 'Enter' || e.nativeEvent.isComposing || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return false;
  const target = e.target;
  if (!(target instanceof HTMLInputElement) || (target.type !== 'text' && target.type !== 'search')) return false;
  return target.getAttribute('aria-invalid') !== 'true' && target.value.trim() !== '';
}

/** Default insert length: one bar of the editor meter at the insert point. */
function barLength(services: AppServices, at: Beat): Beat {
  const meter = services.store?.doc.timing.meter ?? [];
  let bpb = 4;
  for (const mk of meter) if (!Beats.lt(at, mk.t)) bpb = mk.beatsPerBar;
  return Beats.fromInt(Math.max(1, Math.round(bpb)));
}

function InsertTimeDialog(props: {
  readonly services: AppServices;
  readonly dialog: Extract<LaneDialog, { kind: 'insertTime' }>;
}) {
  const { services, dialog } = props;
  const t = useT();
  const [length, setLength] = useState<Beat>(() => barLength(services, dialog.at));
  // The length the field committed last, read by Enter right after the field's own Enter handler committed it.
  const committed = useRef<Beat>(length);
  const [kind, setKind] = useState<ScopeKind>('all');
  const [chosen, setChosen] = useState<readonly LineId[]>([]);
  const active = activeLineOf(services);
  const scope = scopeOf(kind, active, chosen);
  const lengthOk = Beats.lt(Beats.ZERO, length);
  const store = services.store;
  const ctx = useMemo(() => {
    const syntax = services.settings.value.editing.beatSyntax;
    const snapDiv = services.editor.getState().snap.div;
    return store === null ? { snapDiv, syntax } : { snapDiv, syntax, tempo: tempoOfStore(store) };
  }, [services, store]);
  const at = beatText(services, dialog.at);
  const submit = (len: Beat): void => {
    if (scope !== null && Beats.lt(Beats.ZERO, len)) dialog.resolve({ length: len, scope });
  };
  return (
    <Dialog
      open
      size="sm"
      title={t(m.insertTitle)}
      description={kind === 'all' ? t(m.insertBody, { beat: at }) : t(m.insertBodyLines, { beat: at })}
      onClose={() => {
        dialog.resolve(null);
      }}
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              dialog.resolve(null);
            }}
          >
            {t(m.cancel)}
          </Button>
          <Button
            variant="primary"
            disabledReason={!lengthOk ? { key: m.invalidLength } : scope === null ? { key: m.reasonNoLine } : null}
            onClick={() => {
              submit(length);
            }}
          >
            {t(m.insertConfirm)}
          </Button>
        </>
      }
    >
      <div
        className={s.body}
        onKeyDown={(e) => {
          if (!isConfirmEnter(e)) return;
          e.preventDefault();
          submit(committed.current);
        }}
      >
        <BeatField
          label={t(m.insertLength)}
          value={{ kind: 'value', value: length }}
          ctx={ctx}
          stacked
          echo
          autoFocus
          onCommit={(b) => {
            committed.current = b;
            setLength(b);
          }}
        />
        <ScopeChooser services={services} kind={kind} chosen={chosen} onKind={setKind} onChosen={setChosen} />
      </div>
    </Dialog>
  );
}

function RemoveTimeDialog(props: {
  readonly services: AppServices;
  readonly dialog: Extract<LaneDialog, { kind: 'removeTime' }>;
}) {
  const { services, dialog } = props;
  const t = useT();
  const [kind, setKind] = useState<ScopeKind>('all');
  const [chosen, setChosen] = useState<readonly LineId[]>([]);
  const scope = scopeOf(kind, activeLineOf(services), chosen);
  const removed = beatCountText(Beats.sub(dialog.to, dialog.from));
  return (
    <Dialog
      open
      size="sm"
      title={t(m.removeTitle)}
      description={t(m.removeBody, {
        from: beatText(services, dialog.from),
        to: beatText(services, dialog.to),
        beats: removed.text,
        one: removed.one,
      })}
      onClose={() => {
        dialog.resolve(null);
      }}
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              dialog.resolve(null);
            }}
          >
            {t(m.cancel)}
          </Button>
          <Button
            variant="primary"
            disabledReason={scope === null ? { key: m.reasonNoLine } : null}
            onClick={() => {
              if (scope !== null) dialog.resolve({ scope });
            }}
          >
            {t(m.removeConfirm)}
          </Button>
        </>
      }
    >
      <div className={s.body}>
        <ScopeChooser services={services} kind={kind} chosen={chosen} onKind={setKind} onChosen={setChosen} />
      </div>
    </Dialog>
  );
}

function RenameMarkerDialog(props: { readonly dialog: Extract<LaneDialog, { kind: 'renameMarker' }> }) {
  const { dialog } = props;
  const t = useT();
  const [name, setName] = useState(dialog.name);
  // The text as typed, read by Enter (the field reports every keystroke through onChange).
  const typed = useRef(dialog.name);
  const valid = name.trim() !== '';
  const submit = (v: string): void => {
    if (v.trim() !== '') dialog.resolve(v.trim());
  };
  return (
    <Dialog
      open
      size="sm"
      title={t(m.renameTitle)}
      onClose={() => {
        dialog.resolve(null);
      }}
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              dialog.resolve(null);
            }}
          >
            {t(m.cancel)}
          </Button>
          <Button
            variant="primary"
            disabledReason={valid ? null : { key: m.renameEmpty }}
            onClick={() => {
              submit(name);
            }}
          >
            {t(m.renameConfirm)}
          </Button>
        </>
      }
    >
      <div
        className={s.body}
        onKeyDown={(e) => {
          if (!isConfirmEnter(e)) return;
          e.preventDefault();
          submit(typed.current);
        }}
      >
        <TextField
          label={t(m.renameLabel)}
          value={name}
          autoFocus
          maxLength={200}
          onChange={(v) => {
            typed.current = v;
            setName(v);
          }}
          onCommit={(v) => {
            typed.current = v;
            setName(v);
          }}
        />
      </div>
    </Dialog>
  );
}

/** A fresh React key per dialog request, so a request that replaces an open one starts from its own defaults. */
const dialogKeys = new WeakMap<LaneDialog, string>();
let nextDialogKey = 1;
function dialogKey(d: LaneDialog): string {
  let k = dialogKeys.get(d);
  if (k === undefined) {
    k = `lane-dialog-${String(nextDialogKey++)}`;
    dialogKeys.set(d, k);
  }
  return k;
}

/** The open package dialog, if any (the caller re-renders on session changes: LaneShellHost). */
export function LaneDialogs(props: { readonly services: AppServices }) {
  const dialog = laneSession(props.services).dialog;
  if (dialog === null) return null;
  const key = dialogKey(dialog);
  switch (dialog.kind) {
    case 'insertTime':
      return <InsertTimeDialog key={key} services={props.services} dialog={dialog} />;
    case 'removeTime':
      return <RemoveTimeDialog key={key} services={props.services} dialog={dialog} />;
    case 'renameMarker':
      return <RenameMarkerDialog key={key} dialog={dialog} />;
  }
}
